import { useEffect, useState } from 'react';
import { LineChart } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';

interface RevenuePoint {
  weekEnding: string;
  totalValue: number;
}

interface RevenueEvolution {
  historical: RevenuePoint[];
  forecast: RevenuePoint[];
  growthPercent: number | null;
}

function fmtCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toFixed(0);
}

function fmtFull(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

/**
 * Story 57.1 — owner-facing revenue evolution + forward projection.
 * Last 12 weeks of WON revenue (by expected close) plus a 4-week
 * weighted-pipeline forecast. Sparkline-style bars so the trend reads
 * at a glance, no extra chart library required.
 */
export function RevenueEvolutionWidget() {
  const [data, setData] = useState<RevenueEvolution | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get<RevenueEvolution>('/analytics/revenue-evolution', { params: { weeks: 12 } })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
        Loading revenue evolution…
      </div>
    );
  }
  if (!data) return null;

  const allPoints = [...data.historical, ...data.forecast];
  const maxValue = Math.max(1, ...allPoints.map((p) => Number(p.totalValue) || 0));
  const totalRecent = data.historical.reduce(
    (sum, p) => sum + (Number(p.totalValue) || 0),
    0,
  );
  const totalForward = data.forecast.reduce(
    (sum, p) => sum + (Number(p.totalValue) || 0),
    0,
  );

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LineChart className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Revenue evolution
          </h3>
        </div>
        {data.growthPercent !== null && (
          <span
            className={`text-xs font-semibold ${
              Number(data.growthPercent) >= 0 ? 'text-emerald-300' : 'text-rose-300'
            }`}
          >
            {Number(data.growthPercent) >= 0 ? '+' : ''}
            {(Number(data.growthPercent) * 100).toFixed(0)}% vs last quarter
          </span>
        )}
      </div>
      <div className="flex items-end gap-1 border-b pb-2 pt-2" style={{ minHeight: 80 }}>
        {data.historical.map((p) => {
          const v = Number(p.totalValue) || 0;
          const h = (v / maxValue) * 70;
          return (
            <div
              key={`h-${p.weekEnding}`}
              className="flex-1 rounded-t bg-emerald-500/70"
              style={{ height: `${Math.max(h, 2)}px` }}
              title={`${p.weekEnding}: ${fmtFull(v)}`}
            />
          );
        })}
        <div className="mx-1 h-16 w-px bg-border" aria-hidden />
        {data.forecast.map((p) => {
          const v = Number(p.totalValue) || 0;
          const h = (v / maxValue) * 70;
          return (
            <div
              key={`f-${p.weekEnding}`}
              className="flex-1 rounded-t border-2 border-dashed border-border bg-muted/60"
              style={{ height: `${Math.max(h, 2)}px` }}
              title={`${p.weekEnding}: ${fmtFull(v)} (projected)`}
            />
          );
        })}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
        <div>
          <span className="text-muted-foreground">Last 12 weeks won: </span>
          <span className="font-semibold">{fmtCompact(totalRecent)}</span>
        </div>
        <div className="text-right">
          <span className="text-muted-foreground">Next 4 weeks projected: </span>
          <span className="font-semibold">{fmtCompact(totalForward)}</span>
        </div>
      </div>
    </div>
  );
}
